/**
 * CommentInput.jsx
 * Sprint 4 – Enterprise Comments
 *
 * Input box with:
 *   - Subtask selector (optional badge)
 *   - @mention autocomplete dropdown
 *   - Ctrl/Cmd+Enter or Send button to submit
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';

// ── Avatar initials helper ────────────────────────────────────────────────────
function initials(name = '') {
  return name.split(' ').map(p => p[0] || '').join('').toUpperCase().slice(0, 2);
}

const ROLE_COLOR = {
  Manager:   'bg-purple-100 text-purple-700',
  admin:     'bg-red-100 text-red-700',
  superuser: 'bg-orange-100 text-orange-700',
  employee:  'bg-blue-100 text-blue-700',
};
function avatarColor(role) {
  return ROLE_COLOR[role] || 'bg-gray-100 text-gray-600';
}

// ─────────────────────────────────────────────────────────────────────────────

export default function CommentInput({
  onSend,
  sending,
  members = [],      // [{ id, first_name, last_name, role }]
  subtasks = [],     // [{ id, title, status }]
  placeholder = 'Add a comment… (use @ to mention)',
  replyMode = false, // true when replying — hides subtask selector
  lockedSubtask = null, // { id, title } — when set, subtask context is fixed; selector hidden
}) {
  const [text, setText]               = useState('');
  // When lockedSubtask is set, selectedSubtask is always that subtask — no user control
  const [selectedSubtask, setSelectedSubtask] = useState(lockedSubtask || null);
  const [mentionQuery, setMentionQuery]       = useState('');
  const [mentionDropdown, setMentionDropdown] = useState(false);
  const [mentionStart, setMentionStart]       = useState(-1);
  const [pendingMentions, setPendingMentions] = useState([]); // [{ id, display }]
  const [dropdownIndex, setDropdownIndex]     = useState(0);
  const taRef    = useRef();
  const dropRef  = useRef();

  // Focus textarea on mount / after send
  useEffect(() => { if (!sending) taRef.current?.focus(); }, [sending]);

  // Sync selectedSubtask when lockedSubtask prop changes
  useEffect(() => { setSelectedSubtask(lockedSubtask || null); }, [lockedSubtask]);

  // Close dropdown on outside click
  useEffect(() => {
    function onDown(e) {
      if (dropRef.current && !dropRef.current.contains(e.target)) {
        setMentionDropdown(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // Filter members by mentionQuery
  const filteredMembers = mentionQuery
    ? members.filter(m => {
        const full = `${m.first_name} ${m.last_name}`.toLowerCase();
        return full.includes(mentionQuery.toLowerCase());
      })
    : members;

  // ── Text change handler with @mention detection ───────────────────────────
  const handleChange = (e) => {
    const val = e.target.value;
    setText(val);

    const cursor = e.target.selectionStart;
    // Find the most recent @ before the cursor on the same line
    const beforeCursor = val.slice(0, cursor);
    const atIdx = beforeCursor.lastIndexOf('@');

    if (atIdx !== -1) {
      const after = beforeCursor.slice(atIdx + 1);
      // Only open dropdown if no space after @
      if (!/\s/.test(after)) {
        setMentionQuery(after);
        setMentionStart(atIdx);
        setMentionDropdown(true);
        setDropdownIndex(0);
        return;
      }
    }
    setMentionDropdown(false);
  };

  // ── Pick a mention from dropdown ─────────────────────────────────────────
  const pickMention = useCallback((member) => {
    const display = `${member.first_name} ${member.last_name}`;
    const before  = text.slice(0, mentionStart);
    const after   = text.slice(taRef.current?.selectionStart || 0);
    const newText = `${before}@${display} ${after}`;
    setText(newText);
    setPendingMentions(prev => {
      const exists = prev.find(m => m.id === member.id);
      return exists ? prev : [...prev, { id: member.id, display }];
    });
    setMentionDropdown(false);
    setMentionQuery('');
    // Restore focus
    setTimeout(() => {
      if (taRef.current) {
        const pos = before.length + display.length + 2; // @+display+space
        taRef.current.focus();
        taRef.current.setSelectionRange(pos, pos);
      }
    }, 0);
  }, [text, mentionStart]);

  // ── Keyboard navigation in dropdown ──────────────────────────────────────
  const handleKeyDown = (e) => {
    if (mentionDropdown && filteredMembers.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setDropdownIndex(i => Math.min(i + 1, filteredMembers.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setDropdownIndex(i => Math.max(i - 1, 0));
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        pickMention(filteredMembers[dropdownIndex]);
        return;
      }
      if (e.key === 'Escape') {
        setMentionDropdown(false);
        return;
      }
    }

    // Ctrl/Cmd+Enter submits
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;

    // Collect confirmed mention IDs (only those whose @Name still appears in text)
    const confirmedMentions = pendingMentions
      .filter(m => text.includes(`@${m.display}`))
      .map(m => m.id);

    onSend({
      message:    trimmed,
      subtask_id: selectedSubtask?.id || null,
      mentions:   confirmedMentions,
    });

    setText('');
    setPendingMentions([]);
    // Keep subtask selection for multiple comments in the same subtask context
  };

  // ── Render text with @mentions highlighted (preview inside textarea not possible,
  //    so we show a live preview pill row instead) ───────────────────────────
  const mentionPills = pendingMentions.filter(m => text.includes(`@${m.display}`));

  return (
    <div className="border-t border-gray-200 bg-white px-4 pt-3 pb-4">

      {/* ── Subtask selector (top-level comments only, not when locked) ── */}
      {!replyMode && !lockedSubtask && subtasks.length > 0 && (
        <div className="mb-2 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-400 font-medium">Related subtask:</span>
          <button
            type="button"
            onClick={() => setSelectedSubtask(null)}
            className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition ${
              !selectedSubtask
                ? 'bg-gray-800 text-white border-gray-800'
                : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'
            }`}
          >
            General Task
          </button>
          {subtasks.map(st => (
            <button
              key={st.id}
              type="button"
              onClick={() => setSelectedSubtask(st)}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition ${
                selectedSubtask?.id === st.id
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-gray-500 border-gray-200 hover:border-indigo-400'
              }`}
            >
              {st.title}
            </button>
          ))}
        </div>
      )}

      {/* ── Locked subtask badge (read-only, shown when opened from a subtask) ── */}
      {!replyMode && lockedSubtask && (
        <div className="mb-2 flex items-center gap-2">
          <span className="text-xs text-gray-400 font-medium">Commenting on:</span>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs font-semibold">
            <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 inline-block" />
            Subtask: {lockedSubtask.title}
            <span className="ml-1 text-indigo-400 text-xs">(locked)</span>
          </div>
        </div>
      )}

      {/* ── Active subtask badge (when selector chosen, not locked) ── */}
      {!lockedSubtask && selectedSubtask && (
        <div className="mb-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs font-semibold">
          <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 inline-block" />
          Subtask: {selectedSubtask.title}
          <button
            type="button"
            onClick={() => setSelectedSubtask(null)}
            className="ml-1 text-indigo-400 hover:text-indigo-700 leading-none"
            aria-label="Remove subtask"
          >×</button>
        </div>
      )}

      {/* ── @mention pills (confirmed, still in text) ── */}
      {mentionPills.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1">
          {mentionPills.map(m => (
            <span key={m.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#edf5e0] text-[#4a7a20] text-xs font-semibold">
              @{m.display}
            </span>
          ))}
        </div>
      )}

      {/* ── Textarea + send button ── */}
      <div className="relative flex items-end gap-2">
        <div className="relative flex-1" ref={dropRef}>
          <textarea
            ref={taRef}
            value={text}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={replyMode ? 2 : 3}
            disabled={sending}
            className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#7AA33E] focus:border-transparent focus:bg-white resize-none transition disabled:opacity-50"
          />

          {/* @mention dropdown */}
          {mentionDropdown && filteredMembers.length > 0 && (
            <div className="absolute bottom-full left-0 mb-1 w-64 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
              <div className="px-3 py-1.5 border-b border-gray-100 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                Mention a teammate
              </div>
              <ul className="max-h-48 overflow-y-auto">
                {filteredMembers.map((m, i) => {
                  const name = `${m.first_name} ${m.last_name}`;
                  return (
                    <li key={m.id}>
                      <button
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); pickMention(m); }}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition ${
                          i === dropdownIndex ? 'bg-[#f0f7e8]' : 'hover:bg-gray-50'
                        }`}
                      >
                        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${avatarColor(m.role)}`}>
                          {initials(name)}
                        </span>
                        <span>
                          <span className="font-semibold text-gray-800">{name}</span>
                          {m.role && (
                            <span className="ml-1.5 text-xs text-gray-400">{m.role}</span>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={handleSend}
          disabled={!text.trim() || sending}
          className="flex-shrink-0 px-4 py-2.5 rounded-xl bg-[#7AA33E] text-white text-sm font-semibold hover:bg-[#6a9335] active:scale-[0.97] transition disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {sending ? (
            <span className="flex items-center gap-1.5">
              <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" />
              Sending
            </span>
          ) : (
            replyMode ? 'Reply' : 'Send'
          )}
        </button>
      </div>

      <p className="mt-1.5 text-xs text-gray-400">
        Press <kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-500 font-mono text-xs">Ctrl+Enter</kbd> to send · Type <kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-500 font-mono text-xs">@</kbd> to mention
      </p>
    </div>
  );
}
